import { ApiClient } from 'services/api-client'

export interface CrmConnection {
	id: string
	provider: 'hubspot'
	name: string
	portal_id: string
	status: 'ACTIVE' | 'NEEDS_REAUTH'
	connected_by: string
	connected_by_name?: string
	credential_identity?: string
	created_at: string
	updated_at: string
	generation: number
	property: { status: string; name?: string | null; checked_at?: string | null }
}
export interface CrmFormReference {
	id: string
	form_name: string
	asset_id: string
	settings_version: number
}
export const createCrmApi = (accountId: string) => {
	const base = `/accounts/${encodeURIComponent(accountId)}/crm-connections`
	const get = async <T>(path: string, params?: object): Promise<T> =>
		(await ApiClient.client.get<{ data: T }>(base + path, { params })).data.data
	const post = async <T>(path: string, body: unknown): Promise<T> =>
		(await ApiClient.client.post<{ data: T }>(base + path, body)).data.data
	return {
		list: () => get<{ connections: CrmConnection[] }>(''),
		detail: (id: string) =>
			get<CrmConnection & { forms: CrmFormReference[] }>(
				`/${encodeURIComponent(id)}`
			),
		start: (connection_id?: string) =>
			post<{ url: string; state: string }>('/oauth/start', { connection_id }),
		complete: (code: string, state: string) =>
			post<CrmConnection>('/oauth/complete', { code, state }),
		verify: (id: string, asset_id: string, form_id?: string) =>
			post<{
				status: 'valid' | 'missing' | 'invalid'
				property_name: string | null
				verification_token: string
			}>(`/${encodeURIComponent(id)}/verify`, { asset_id, form_id }),
		impact: (id: string) =>
			get<{ forms: CrmFormReference[] }>(`/${encodeURIComponent(id)}/impact`),
		remove: (id: string) =>
			ApiClient.client.delete(`${base}/${encodeURIComponent(id)}`),
		request: (message: string) => post('/requests', { message }),
		preference: (assetId: string) =>
			get<{ connection_id: string | null; updated_at: string | null }>(
				`/preferences/${encodeURIComponent(assetId)}`
			),
		setPreference: (assetId: string, connection_id: string | null) =>
			ApiClient.client.put(
				`${base}/preferences/${encodeURIComponent(assetId)}`,
				{ connection_id }
			),
		defaultImpact: (assetId: string, connection_id: string) =>
			get<{ forms: CrmFormReference[] }>(
				`/preferences/${encodeURIComponent(assetId)}/impact`,
				{ connection_id }
			),
		applyDefault: (
			assetId: string,
			connection_id: string,
			forms: Array<{ id: string; settings_version: number }>
		) =>
			post<{ updated: number; skipped: number }>(
				`/preferences/${encodeURIComponent(assetId)}/apply`,
				{ connection_id, forms }
			)
	}
}
export type CrmApi = ReturnType<typeof createCrmApi>
